// modals/sales/ViewSale.jsx
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { 
    Printer, 
    Download, 
    Package,
    TrendingUp,
    CheckCircle,
    XCircle,
    AlertTriangle,
    X
} from 'react-feather';
import dayjs from 'dayjs';
import Swal from 'sweetalert2';
import { saleActions } from '../../action/saleActions';

const ViewSale = ({ sale, onClose }) => {
    const [saleData, setSaleData] = useState(sale);
    const [items, setItems] = useState([]);
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingPayments, setLoadingPayments] = useState(false);
    const [activeTab, setActiveTab] = useState('items');

    useEffect(() => {
        loadSaleDetails();
        loadPaymentHistory();
    }, [sale]);

    const loadSaleDetails = async () => {
        try {
            setLoading(true);
            const result = await saleActions.getSale(sale.id);
            
            if (result.success) {
                // Handle different response structures
                const responseData = result.data;
                
                // Set sale data - could be in responseData.sale or directly in responseData
                if (responseData.sale) {
                    setSaleData(responseData.sale);
                    setItems(responseData.items || []);
                } else {
                    setSaleData(responseData);
                    setItems(responseData.items || []);
                }
            } else {
                console.error('Failed to load sale details:', result.message);
            }
        } catch (error) {
            console.error('Error loading sale details:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadPaymentHistory = async () => {
        try {
            setLoadingPayments(true);
            const result = await saleActions.getPaymentHistory(sale.id, 1, 100);
            
            if (result.success) {
                setPayments(result.data.payments || []);
            }
        } catch (error) {
            console.error('Error loading payment history:', error);
        } finally {
            setLoadingPayments(false);
        }
    };

    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(num);
    };

    const formatDate = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY') : 'N/A';
    };

    const formatDateTime = (date) => {
        return date ? dayjs(date).format('DD/MM/YYYY hh:mm A') : 'N/A';
    };

    const getStatusBadge = (status) => {
        const badges = {
            'pending': { class: 'bg-warning', icon: <AlertTriangle size={14} /> },
            'confirmed': { class: 'bg-info', icon: <CheckCircle size={14} /> },
            'processing': { class: 'bg-primary', icon: <TrendingUp size={14} /> },
            'shipped': { class: 'bg-secondary', icon: <Package size={14} /> },
            'delivered': { class: 'bg-success', icon: <CheckCircle size={14} /> },
            'cancelled': { class: 'bg-danger', icon: <XCircle size={14} /> }
        };
        const badge = badges[status?.toLowerCase()] || { class: 'bg-secondary', icon: null };
        return (
            <span className={`badge ${badge.class}`}>
                {badge.icon} {status?.toUpperCase()}
            </span>
        );
    };

    const getPaymentStatusBadge = (status) => {
        const badges = {
            'paid': { class: 'bg-success', icon: <CheckCircle size={14} /> },
            'partial': { class: 'bg-warning', icon: <AlertTriangle size={14} /> },
            'unpaid': { class: 'bg-danger', icon: <XCircle size={14} /> }
        };
        const badge = badges[status?.toLowerCase()] || { class: 'bg-secondary', icon: null };
        return (
            <span className={`badge ${badge.class}`}>
                {badge.icon} {status?.toUpperCase()}
            </span>
        );
    };

    const getPaymentMethodBadge = (method) => {
        const badges = {
            'cash': 'bg-success',
            'bank_transfer': 'bg-info',
            'cheque': 'bg-warning',
            'credit_card': 'bg-primary',
            'upi': 'bg-secondary'
        };
        return badges[method?.toLowerCase()] || 'bg-secondary';
    };

    const handlePrint = () => {
        // Implement print functionality
        window.print();
    };

    const handleDownload = async () => {
        try {
            const result = await saleActions.getInvoice(sale.id);
            if (result.success && result.data) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `invoice_${saleData.invoice_no}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
            } else {
                Swal.fire('Error', result.message || 'Failed to download invoice', 'error');
            }
        } catch (error) {
            console.error('Error downloading invoice:', error);
            Swal.fire('Error', 'Failed to download invoice', 'error');
        }
    };

    // Calculate totals from items if not provided in saleData
    const calculateSubtotal = () => {
        return items.reduce((sum, item) => sum + (parseFloat(item.unit_price || 0) * parseFloat(item.quantity || 0)), 0);
    };

    const calculateTax = () => {
        return items.reduce((sum, item) => sum + parseFloat(item.tax_amount || 0), 0);
    };

    const calculateDiscount = () => {
        return items.reduce((sum, item) => sum + parseFloat(item.discount_amount || 0), 0);
    };

    const subtotal = saleData.subtotal || calculateSubtotal();
    const taxTotal = saleData.tax || calculateTax();
    const discountTotal = saleData.discount || calculateDiscount();
    const grandTotal = saleData.grand_total || (subtotal - discountTotal + taxTotal + parseFloat(saleData.shipping || 0));

    if (!saleData) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Sale Details - {saleData.invoice_no || 'N/A'}
                        </h5>
                        <div className="d-flex gap-2">
                            <button 
                                type="button" 
                                className="btn btn-outline-primary btn-sm"
                                onClick={handlePrint}
                                title="Print Invoice"
                            >
                                <Printer size={16} />
                            </button>
                            <button 
                                type="button" 
                                className="btn btn-outline-success btn-sm"
                                onClick={handleDownload}
                                title="Download Invoice"
                            >
                                <Download size={16} />
                            </button>
                            <button 
                                type="button" 
                                className="btn-close" 
                                onClick={onClose}
                            ></button>
                        </div>
                    </div>
                    
                    <div className="modal-body">
                        {loading ? (
                            <div className="text-center p-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-3">Loading details...</p>
                            </div>
                        ) : (
                            <>
                                {/* Header Info */}
                                <div className="row mb-4">
                                    <div className="col-md-8">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Invoice No</p>
                                                        <h6 className="fw-bold">{saleData.invoice_no || 'N/A'}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Date</p>
                                                        <h6>{formatDate(saleData.sale_date)}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">PO Number</p>
                                                        <h6>{saleData.po_number || '-'}</h6>
                                                    </div>
                                                </div>
                                                <div className="row mt-2">
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">DC Number</p>
                                                        <h6>{saleData.dc_number || '-'}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Created By</p>
                                                        <h6>{saleData.created_by || 'Admin'}</h6>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <p className="mb-1 text-muted">Created At</p>
                                                        <h6>{formatDateTime(saleData.created_at)}</h6>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-4">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <div>
                                                        <p className="mb-1 text-muted">Status</p>
                                                        {getStatusBadge(saleData.status)}
                                                    </div>
                                                    <div>
                                                        <p className="mb-1 text-muted">Payment</p>
                                                        {getPaymentStatusBadge(saleData.payment_status)}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Customer Info */}
                                <div className="card mb-4">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">Customer Information</h6>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-md-3">
                                                <p className="mb-1 text-muted">Name</p>
                                                <p className="fw-bold">{saleData.customer_name || 'N/A'}</p>
                                            </div>
                                            <div className="col-md-3">
                                                <p className="mb-1 text-muted">Phone</p>
                                                <p>{saleData.customer_phone || 'N/A'}</p>
                                            </div>
                                            <div className="col-md-3">
                                                <p className="mb-1 text-muted">Email</p>
                                                <p>{saleData.customer_email || 'N/A'}</p>
                                            </div>
                                            <div className="col-md-3">
                                                <p className="mb-1 text-muted">Address</p>
                                                <p>{saleData.customer_address || 'N/A'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Tabs */}
                                <ul className="nav nav-tabs mb-3">
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'items' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('items')}
                                        >
                                            Items ({items.length})
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link ${activeTab === 'payments' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('payments')}
                                        >
                                            Payment History ({payments.length})
                                        </button>
                                    </li>
                                </ul>

                                {/* Tab Content */}
                                <div className="tab-content">
                                    {/* Items Tab */}
                                    {activeTab === 'items' && (
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-hover">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>#</th>
                                                        <th>Product Code</th>
                                                        <th>Product Name</th>
                                                        <th>Units</th>
                                                        <th className="text-end">Qty</th>
                                                        <th className="text-end">Rate</th>
                                                        <th className="text-end">Discount</th>
                                                        <th className="text-end">GST %</th>
                                                        <th className="text-end">Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {items.length > 0 ? (
                                                        items.map((item, index) => (
                                                            <tr key={item.id || index}>
                                                                <td>{index + 1}</td>
                                                                <td>{item.sku || item.product_code || '-'}</td>
                                                                <td>
                                                                    <strong>{item.product_name}</strong>
                                                                </td>
                                                                <td>{item.units || 'PCS'}</td>
                                                                <td className="text-end">{parseFloat(item.quantity || 0).toFixed(2)}</td>
                                                                <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                                <td className="text-end">{item.discount || 0}%</td>
                                                                <td className="text-end">{item.tax_percent || 0}%</td>
                                                                <td className="text-end fw-bold">{formatCurrency(item.total_cost)}</td>
                                                            </tr>
                                                        ))
                                                    ) : (
                                                        <tr>
                                                            <td colSpan="9" className="text-center py-4">
                                                                No items found
                                                            </td>
                                                        </tr>
                                                    )}
                                                </tbody>
                                                <tfoot className="table-light">
                                                    <tr>
                                                        <th colSpan="8" className="text-end">Subtotal:</th>
                                                        <th className="text-end">{formatCurrency(subtotal)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="8" className="text-end">Discount:</th>
                                                        <th className="text-end text-danger">-{formatCurrency(discountTotal)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="8" className="text-end">Tax Total:</th>
                                                        <th className="text-end">{formatCurrency(taxTotal)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="8" className="text-end">Shipping:</th>
                                                        <th className="text-end">{formatCurrency(saleData.shipping || 0)}</th>
                                                    </tr>
                                                    <tr>
                                                        <th colSpan="8" className="text-end fw-bold">Grand Total:</th>
                                                        <th className="text-end fw-bold text-primary">{formatCurrency(grandTotal)}</th>
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </div>
                                    )}

                                    {/* Payments Tab */}
                                    {activeTab === 'payments' && (
                                        <div>
                                            {loadingPayments ? (
                                                <div className="text-center p-4">
                                                    <div className="spinner-border text-primary" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="row mb-3">
                                                        <div className="col-md-3">
                                                            <div className="card bg-light">
                                                                <div className="card-body py-2">
                                                                    <small className="text-muted">Total Paid</small>
                                                                    <h6 className="mb-0 text-success">{formatCurrency(saleData.paid_amount || 0)}</h6>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <div className="card bg-light">
                                                                <div className="card-body py-2">
                                                                    <small className="text-muted">Due Amount</small>
                                                                    <h6 className="mb-0 text-danger">{formatCurrency(saleData.due_amount || 0)}</h6>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <div className="card bg-light">
                                                                <div className="card-body py-2">
                                                                    <small className="text-muted">Payment Mode</small>
                                                                    <h6 className="mb-0 text-capitalize">{saleData.payment_mode || 'N/A'}</h6>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="col-md-3">
                                                            <div className="card bg-light">
                                                                <div className="card-body py-2">
                                                                    <small className="text-muted">Total Payments</small>
                                                                    <h6 className="mb-0">{payments.length}</h6>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="table-responsive">
                                                        <table className="table table-sm table-hover">
                                                            <thead className="table-light">
                                                                <tr>
                                                                    <th>#</th>
                                                                    <th>Date</th>
                                                                    <th>Amount</th>
                                                                    <th>Method</th>
                                                                    <th>Reference</th>
                                                                    <th>Notes</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {payments.length === 0 ? (
                                                                    <tr>
                                                                        <td colSpan="6" className="text-center py-4">
                                                                            No payment records found
                                                                        </td>
                                                                    </tr>
                                                                ) : (
                                                                    payments.map((payment, index) => (
                                                                        <tr key={payment.id || index}>
                                                                            <td>{index + 1}</td>
                                                                            <td>{formatDate(payment.payment_date)}</td>
                                                                            <td className="text-success fw-bold">{formatCurrency(payment.amount)}</td>
                                                                            <td>
                                                                                <span className={`badge ${getPaymentMethodBadge(payment.payment_method)}`}>
                                                                                    {payment.payment_method}
                                                                                </span>
                                                                            </td>
                                                                            <td>{payment.reference_no || '-'}</td>
                                                                            <td>{payment.notes || '-'}</td>
                                                                        </tr>
                                                                    ))
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Remarks */}
                                {saleData.remarks && (
                                    <div className="card mt-3">
                                        <div className="card-header bg-light">
                                            <h6 className="mb-0">Remarks</h6>
                                        </div>
                                        <div className="card-body">
                                            <p className="mb-0">{saleData.remarks}</p>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    
                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            <X size={14} className="me-2" />
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

ViewSale.propTypes = {
    sale: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
};

export default ViewSale;